import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronsDown, Triangle, ChevronRight, LayoutList, PhoneCall } from "lucide-react";
import { fetchNavData } from "@/components/navData";

const ARROW_ICONS = { chevron: ChevronDown, double: ChevronsDown };

export default function MegaMenu({ onCallback, label = "Programs", link = "/courses", showArrow = true, arrowStyle = "chevron" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const closeTimer = useRef(null);

  useEffect(() => { fetchNavData().then(setData); }, []);

  const enter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpen(false), 180); };

  const domains = data?.domains || [];
  const current = domains[active] || domains[0];

  const triggerClick = () => {
    if (/^https?:\/\//i.test(link)) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      setOpen(false);
      navigate(link || "/courses");
    }
  };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave} data-testid="mega-menu">
      <button onClick={triggerClick} className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${open ? "text-rose-600" : "text-slate-700 hover:text-rose-600"}`} data-testid="mega-menu-trigger">
        {label} {showArrow && (arrowStyle === "caret"
          ? <Triangle className={`w-2.5 h-2.5 fill-current transition-transform ${open ? "" : "rotate-180"}`} data-testid="mega-menu-arrow" />
          : (() => { const A = ARROW_ICONS[arrowStyle] || ChevronDown; return <A className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} data-testid="mega-menu-arrow" />; })())}
      </button>

      {open && data && (
        <div className="absolute left-0 top-full pt-3 z-50" data-testid="mega-menu-panel">
          <div className="w-[940px] max-w-[94vw] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden grid grid-cols-12">
            <div className="col-span-4 bg-slate-50/70 border-r border-slate-100 py-5 max-h-[520px] overflow-y-auto">
              <p className="flex items-center gap-2 px-6 pb-4 text-base font-extrabold text-slate-900">
                <LayoutList className="w-4 h-4" /> Domains
              </p>
              {domains.map((dom, i) => (
                <button key={dom.slug} onMouseEnter={() => setActive(i)} data-testid={`mega-domain-${dom.slug}`}
                  className={`w-full flex items-center gap-3 px-6 py-3.5 text-left transition-colors ${i === active ? "bg-rose-50 text-rose-600" : "text-slate-800 hover:bg-slate-100"}`}>
                  <span className="text-sm font-semibold flex-1">{dom.name}</span>
                  <ChevronRight className={`w-4 h-4 ${i === active ? "text-rose-500" : "text-slate-300"}`} />
                </button>
              ))}
            </div>

            <div className="col-span-8 p-7 max-h-[520px] overflow-y-auto flex flex-col">
              {current && (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">{current.name}</h3>
                    <Link to={`/courses?q=${encodeURIComponent(current.name)}`} onClick={() => setOpen(false)} data-testid="mega-view-domain"
                      className="text-sm font-bold text-rose-600 hover:text-rose-700 whitespace-nowrap">
                      {current.name} Courses <ChevronRight className="w-4 h-4 inline -mt-0.5" />
                    </Link>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-6 mb-4">{current.name}</p>
                  {(current.programs || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-6" data-testid="mega-empty">Programs coming soon.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-7">
                      {current.programs.map((p) => (
                        <Link key={p.slug} to={p.url_path || `/courses/${p.slug}`} onClick={() => setOpen(false)} data-testid={`mega-program-${p.slug}`} className="group flex gap-3">
                          <span className="w-12 h-12 rounded-lg border border-slate-200 bg-white flex items-center justify-center shrink-0 overflow-hidden">
                            {p.logo
                              ? <img src={p.logo} alt="" className="w-full h-full object-contain p-1" />
                              : <span className="text-xs font-extrabold text-rose-600">{(p.university || "TB").slice(0, 2).toUpperCase()}</span>}
                          </span>
                          <span>
                            <span className="block text-xs text-slate-500">{p.university}</span>
                            <span className="block text-sm font-bold text-slate-900 leading-snug mt-0.5 group-hover:text-rose-600 transition-colors">{p.title}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-8">
                    <div className="border-t border-slate-100 pt-5 flex items-center justify-between gap-4">
                      <p className="text-sm text-slate-500"><span className="font-bold text-slate-800">Confused about what fits?</span> Free 1:1 counseling call.</p>
                      <button onClick={() => { setOpen(false); onCallback?.(); }} data-testid="mega-counselor-cta"
                        className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap">
                        <PhoneCall className="w-4 h-4" /> Talk to a counselor
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
